import React, { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface CategoryItem {
  Id?: number | string;
  id?: number | string;
  Name?: string;
  name?: string;
  title?: string;
  Title?: string;
  ImageUrl?: string;
  image?: string;
  ItemType?: string;
  itemType?: string;
  RedirectUrl?: string | null;
  redirectUrl?: string | null;
}

interface Props {
  items: CategoryItem[];
  onCategoryClick?: (category: CategoryItem) => void;
}

const CategorySlideshow: React.FC<Props> = ({
  items,
  onCategoryClick,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const safeItems = Array.isArray(items) ? items : [];
  const totalItems = safeItems.length;

  // ---------------------------------------------------------
  // CHECK SCROLL POSITION
  // ---------------------------------------------------------
  const checkScrollability = useCallback(() => {
    const element = scrollRef.current;

    if (!element) return;

    const {
      scrollLeft,
      scrollWidth,
      clientWidth,
    } = element;

    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(
      scrollLeft + clientWidth < scrollWidth - 4
    );
  }, []);

  // ---------------------------------------------------------
  // INITIAL SCROLL CHECK
  // ---------------------------------------------------------
  useEffect(() => {
    if (totalItems === 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    // Give iOS/browser layout time to calculate dimensions
    const timer = window.setTimeout(() => {
      checkScrollability();
    }, 100);

    return () => {
      window.clearTimeout(timer);
    };
  }, [totalItems, checkScrollability]);

  // ---------------------------------------------------------
  // RESIZE
  // ---------------------------------------------------------
  useEffect(() => {
    const handleResize = () => {
      checkScrollability();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [checkScrollability]);

  // ---------------------------------------------------------
  // SCROLL
  // ---------------------------------------------------------
  const scroll = (direction: "left" | "right") => {
    const element = scrollRef.current;

    if (!element) return;

    const scrollAmount = element.clientWidth * 0.75;

    element.scrollBy({
      left:
        direction === "left"
          ? -scrollAmount
          : scrollAmount,
      behavior: "smooth",
    });
  };

  // ---------------------------------------------------------
  // ITEM CLICK
  // ---------------------------------------------------------
  const handleItemClick = (item: CategoryItem) => {
    const itemType = (
      item.itemType ??
      item.ItemType ??
      ""
    )
      .trim()
      .toLowerCase();

    const redirectUrl =
      item.redirectUrl ??
      item.RedirectUrl ??
      null;

    if (itemType === "custom") {
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }

      return;
    }

    onCategoryClick?.(item);
  };

  // ---------------------------------------------------------
  // EMPTY
  // ---------------------------------------------------------
  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="w-full max-w-full relative select-none">
      {/* -----------------------------------------------------
          LEFT ARROW
      ----------------------------------------------------- */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scroll("left")}
          aria-label="Scroll left"
          className="
            absolute
            left-1
            md:left-2
            top-[50px]
            -translate-y-1/2
            z-20
            bg-white/95
            backdrop-blur-sm
            border
            border-gray-100
            shadow-md
            rounded-full
            p-2
            text-gray-700
            hover:bg-white
            hover:scale-110
            active:scale-95
            transition-all
            cursor-pointer
          "
        >
          <ChevronLeft size={20} />
        </button>
      )}

      {/* -----------------------------------------------------
          RIGHT ARROW
      ----------------------------------------------------- */}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scroll("right")}
          aria-label="Scroll right"
          className="
            absolute
            right-1
            md:right-2
            top-[50px]
            -translate-y-1/2
            z-20
            bg-white/95
            backdrop-blur-sm
            border
            border-gray-100
            shadow-md
            rounded-full
            p-2
            text-gray-700
            hover:bg-white
            hover:scale-110
            active:scale-95
            transition-all
            cursor-pointer
          "
        >
          <ChevronRight size={20} />
        </button>
      )}

      {/* -----------------------------------------------------
          SCROLL AREA
      ----------------------------------------------------- */}
      <div
        ref={scrollRef}
        onScroll={checkScrollability}
        className="
          flex
          w-full
          max-w-full
          gap-4
          sm:gap-6
          md:gap-8
          overflow-x-auto
          px-4
          md:px-8
          py-2
          snap-x
          snap-mandatory
          scroll-smooth
          [-ms-overflow-style:none]
          [scrollbar-width:none]
          [&::-webkit-scrollbar]:hidden
        "
        style={{
          WebkitOverflowScrolling: "touch",
        }}
      >
        {safeItems.map((item, index) => {
          const itemId =
            item.Id ??
            item.id ??
            index;

          const itemName =
            item.Name ??
            item.title ??
            item.Title ??
            item.name ??
            "Category";

          const itemImg =
            item.ImageUrl ??
            item.image ??
            "";

          return (
            <div
              key={itemId}
              onClick={() => handleItemClick(item)}
              className="
                flex-shrink-0
                w-[90px]
                sm:w-[100px]
                md:w-[110px]
                flex
                flex-col
                items-center
                cursor-pointer
                group
                snap-start
              "
            >
              {/* IMAGE */}
              <div
                className="
                  relative
                  w-full
                  aspect-square
                  bg-slate-50
                  border
                  border-gray-100
                  rounded-2xl
                  flex
                  items-center
                  justify-center
                  shadow-sm
                  overflow-hidden
                  group-hover:shadow-md
                  group-hover:border-gray-200
                  transition-all
                  duration-300
                "
              >
                {itemImg ? (
                  <img
                    src={itemImg}
                    alt={itemName}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className="
                      w-full
                      h-full
                      object-cover
                      rounded-2xl
                      group-hover:scale-105
                      transition-transform
                      duration-300
                    "
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                    No Image
                  </div>
                )}
              </div>

              {/* NAME */}
              <p
                className="
                  text-xs
                  sm:text-sm
                  text-logoHeading
                  mt-2
                  text-center
                  font-medium
                  line-clamp-1
                  w-full
                  px-1
                "
              >
                {itemName}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategorySlideshow;